import { getSupabaseAdmin } from "@/lib/supabaseAdmin";

export const EVENTS = {
  SEARCH_QUERY: "search.query",
  SEARCH_ZERO_RESULTS: "search.zero_results",
  SEARCH_RESULT_SELECTED: "search.result_selected",
  PILL_SCAN_STARTED: "pill_counter.scan_started",
  PILL_SCAN_COMPLETED: "pill_counter.scan_completed",
  PILL_SCAN_FAILED: "pill_counter.scan_failed",
  PILL_COUNT_CONFIRMED: "pill_counter.count_confirmed",
  PILL_COUNT_MANUAL: "pill_counter.manual_count",
  LIVE_SCAN_STARTED: "pill_counter.live_scan_started",
  LIVE_SCAN_STOPPED: "pill_counter.live_scan_stopped",
  LIVE_COUNT_CAPTURED: "pill_counter.live_count_captured",
  CAMERA_PERMISSION_DENIED: "pill_counter.camera_permission_denied",
  OPENCV_LOAD_FAILED: "pill_counter.opencv_load_failed",
  UPGRADE_PROMPT_SHOWN: "upgrade.prompt_shown",
  UPGRADE_PROMPT_CLICKED: "upgrade.prompt_clicked",
  UPGRADE_PROMPT_DISMISSED: "upgrade.prompt_dismissed",
  PRICING_PAGE_VIEWED: "upgrade.pricing_page_viewed",
  UPGRADE_COMPLETED: "upgrade.completed",
  MEDICATION_ADDED: "inventory.medication_added",
  MEDICATION_UPDATED: "inventory.medication_updated",
  MEDICATION_DELETED: "inventory.medication_deleted",
  BULK_IMPORT_STARTED: "inventory.bulk_import_started",
  BULK_IMPORT_COMPLETED: "inventory.bulk_import_completed",
  EXPIRY_REPORT_EXPORTED: "expiry.report_exported",
  EXPIRY_ITEM_ACTIONED: "expiry.item_actioned",
  EXPIRY_ALERT_SENT: "expiry.alert_sent",
  USER_SIGNED_UP: "auth.signed_up",
  USER_SIGNED_IN: "auth.signed_in",
  USER_SIGNED_OUT: "auth.signed_out",
  CHECKOUT_STARTED: "billing.checkout_started",
  SUBSCRIPTION_CREATED: "billing.subscription_created",
  SUBSCRIPTION_CANCELLED: "billing.subscription_cancelled",
  SUBSCRIPTION_UPDATED: "billing.subscription_updated",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export const EVENT_CATEGORIES: Record<EventName, string> = {
  [EVENTS.SEARCH_QUERY]: "search",
  [EVENTS.SEARCH_ZERO_RESULTS]: "search",
  [EVENTS.SEARCH_RESULT_SELECTED]: "search",
  [EVENTS.PILL_SCAN_STARTED]: "pill_counter",
  [EVENTS.PILL_SCAN_COMPLETED]: "pill_counter",
  [EVENTS.PILL_SCAN_FAILED]: "pill_counter",
  [EVENTS.PILL_COUNT_CONFIRMED]: "pill_counter",
  [EVENTS.PILL_COUNT_MANUAL]: "pill_counter",
  [EVENTS.LIVE_SCAN_STARTED]: "pill_counter",
  [EVENTS.LIVE_SCAN_STOPPED]: "pill_counter",
  [EVENTS.LIVE_COUNT_CAPTURED]: "pill_counter",
  [EVENTS.CAMERA_PERMISSION_DENIED]: "pill_counter",
  [EVENTS.OPENCV_LOAD_FAILED]: "pill_counter",
  [EVENTS.UPGRADE_PROMPT_SHOWN]: "upgrade",
  [EVENTS.UPGRADE_PROMPT_CLICKED]: "upgrade",
  [EVENTS.UPGRADE_PROMPT_DISMISSED]: "upgrade",
  [EVENTS.PRICING_PAGE_VIEWED]: "upgrade",
  [EVENTS.UPGRADE_COMPLETED]: "upgrade",
  [EVENTS.MEDICATION_ADDED]: "inventory",
  [EVENTS.MEDICATION_UPDATED]: "inventory",
  [EVENTS.MEDICATION_DELETED]: "inventory",
  [EVENTS.BULK_IMPORT_STARTED]: "inventory",
  [EVENTS.BULK_IMPORT_COMPLETED]: "inventory",
  [EVENTS.EXPIRY_REPORT_EXPORTED]: "expiry",
  [EVENTS.EXPIRY_ITEM_ACTIONED]: "expiry",
  [EVENTS.EXPIRY_ALERT_SENT]: "expiry",
  [EVENTS.USER_SIGNED_UP]: "auth",
  [EVENTS.USER_SIGNED_IN]: "auth",
  [EVENTS.USER_SIGNED_OUT]: "auth",
  [EVENTS.CHECKOUT_STARTED]: "billing",
  [EVENTS.SUBSCRIPTION_CREATED]: "billing",
  [EVENTS.SUBSCRIPTION_CANCELLED]: "billing",
  [EVENTS.SUBSCRIPTION_UPDATED]: "billing",
};

export interface TrackEventInput {
  eventName: EventName;
  userId?: string;
  sessionId?: string;
  plan?: string;
  properties?: Record<string, unknown>;
}

const PII_FIELDS = [
  "email",
  "name",
  "phone",
  "address",
  "password",
  "token",
  "secret",
  "key",
  "firstName",
  "lastName",
  "first_name",
  "last_name",
];

function scrubPII(properties: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (PII_FIELDS.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
      scrubbed[key] = "[redacted]";
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

export async function trackEvent(input: TrackEventInput): Promise<void> {
  try {
    const admin = getSupabaseAdmin();
    const safeProperties = scrubPII(input.properties ?? {});
    await admin.from("product_events").insert({
      user_id: input.userId ?? null,
      session_id: input.sessionId ?? null,
      event_name: input.eventName,
      event_category: EVENT_CATEGORIES[input.eventName],
      properties: safeProperties,
      plan: input.plan ?? null,
      created_at: new Date().toISOString(),
    });
  } catch (error) {
    console.warn("[analytics] Failed to track event:", input.eventName, error);
  }
}
